/* eslint-disable max-len */
/* eslint-disable no-unused-vars */

import { html, PolymerElement } from "@polymer/polymer/polymer-element.js";
import template from "./ns-upload-image.html";
import * as Croppie from "./croppie.js";
import { NsUploadImageCropModal } from "./ns-upload-image-crop-modal/ns-upload-image-crop-modal";
import { NsUploadImageConfirmationModal } from "./ns-upload-image-confirmation-modal/ns-upload-image-confirmation-modal";

/**
 * Allows to crop an image and produce up to three versions of the cropped image (large, medium, small).
 *
 * <h1>Image Cropping</h1>
 * <a href="//developers.dev.nuskin.com/playground/ns-upload-image.html">Click here for DEMO</a>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Custom Events
 *
 *      `<ns-upload-image>` fires following custom events:
 *       <br/>
 *       <br/>
 *
 *      | Custom Event             | Description                      | Returned data                                           |
 *      |:-------------------------|:---------------------------------|:--------------------------------------------------------|
 *      | `ns-image-upload-result` | Fires after successfull cropping | `{[_small: <BLOB>], [_medium: <BLOB>], _large: <BLOB>}` |
 *      | `ns-image-upload-remove` | Fires on REMOVE IMAGE click      | `returns a string with image URL`                       |
 *      | <img width="250"/>       | <img width="250"/>               |                                                         |
 *
 *      ### Styling
 *
 *      `<ns-upload-image>` provides the following custom properties and mixins for styling:
 *       <br/>
 *       <br/>
 *
 *      Custom property               | Description                            | Default
 *      :-----------------------------|:---------------------------------------|:------------------
 *      `--ns-upload-image`           | Mixin for additional wrapper styling   | `{}`
 *      `--ns-upload-image-drop-zone` | Mixin for additional drop zone styling | `{}`
 *      <img width="250"/>            | <img width="250"/>                     |
 *
 *  </script>
 * </marked-element>
 */
export class NsUploadImage extends PolymerElement {
  constructor() {
    super();
    this.file = null;
    this.errors = [];
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get is() {
    return "ns-upload-image";
  }

  static get properties() {
    return {
      /**
       * Property indicates if output image should be a circle ( default: false )
       */
      circle: {
        type: Boolean,
        observer: "_handleChanges"
      },
      /**
       * Property indicates if output image should be a square ( default: true )
       */
      square: {
        type: Boolean,
        value: true,
        observer: "_handleChanges"
      },
      /**
       * Property indicates if uploader should save croped image with it's original dimensions for desktop( default: true )
       */
      large: {
        type: Boolean,
        value: true
      },
      /**
       * Property indicates if uploader should save cropped image with reduced (1x0.66) dimensions for tablets( default: false )
       */
      medium: {
        type: Boolean
      },
      /**
       * Property indicates if uploader should save cropped image with reduced (1x0.33) dimensions for mobile ( default: false )
       */
      small: {
        type: Boolean
      },
      /**
       * Property populates a label for the component with existing image (default: 'Profile Photo')
       */
      label: {
        type: String,
        value: "Profile Photo"
      },
      /**
       * Property specifies if component is required by the parent form (default: false)
       */
      required: {
        type: Boolean,
        value: false
      },
      /**
       * Property specifies if component is disabled (default: true)
       */
      disabled: {
        type: Boolean,
        value: false,
        observer: "_onDisabledChange"
      },
      /**
       * Property specifies allowed maximum width of an image in pixels (default: 300)
       */
      outputWidth: {
        type: Number,
        value: 300
      },
      /**
       * Property specifies allowed maximum height of an image in pixels (default: 300)
       */
      outputHeight: {
        type: Number,
        value: 300
      },
      /**
       * Property specifies allowed maximum size of an image in MB (default: 10)
       */
      maxSize: {
        type: Number,
        value: 10
      },
      /**
       * Property specifies the label text for max file size (default: 'Max File Size')
       */
      maxSizeLabel: {
        type: String,
        value: "Max File Size"
      },
      /**
       * Used for user's profile image if they have one already
       */
      imageUrl: {
        type: String,
        observer: "_userHasImageUrl"
      },
      /**
       * Property specifies the label text for allowed formats (default: 'Allowed')
       */
      allowedFormatsLabel: {
        type: String,
        value: "Allowed Formats:"
      },
      /**
       * Property contains the list of MIME image formats (default: ['image/jpeg', 'image/png', 'image/gif'])
       */
      allowedFormats: {
        type: Array,
        value: ["image/jpeg", "image/png", "image/gif"]
      },
      /**
       * Property specifies the way image uploader will look like. Options:
       * 'profile', 'basic' (default: 'basic')
       */
      theme: {
        type: String,
        value: "basic",
        observer: "_handleThemeChange"
      },
      /**
       * Property specifies the bolded text of the dropping area (default: 'Browse')
       */
      dropZoneBoldText: {
        type: String,
        value: "Browse"
      },
      /**
       * Property specifies the caption text of the dropping area (default: 'Drag an image here or')
       */
      dropZoneCaption: {
        type: String,
        value: "Drag an image here or"
      },
      /**
       * Remove image modal header text (default: 'Remove this photo?')
       */
      removeModalTitle: {
        type: String,
        value: "Remove this photo?"
      },
      /**
       * Remove image modal cancel button text (default: 'Cancel')
       */
      removeModalCancel: {
        type: String,
        value: "Cancel"
      },
      /**
       * Remove image modal confirm button text (default: 'Remove it')
       */
      removeModalConfirm: {
        type: String,
        value: "Remove it"
      },
      /**
       * Crop modal header text (default: 'Please crop your image')
       */
      cropModalHeader: {
        type: String,
        value: "Please crop your image"
      },
      /**
       * Done cropping button caption - step 3 (default: 'Done')
       */
      doneCroppingButtonCaption: {
        type: String,
        value: "Done"
      },
      /**
       * Property specifies the text on 'step 2' of the upload (default: 'Ready to Upload')
       */
      readyToUploadText: {
        type: String,
        value: "Ready to Upload"
      },
      /**
       * Property specifies the text under uploadsed image (default: 'Remove photo')
       */
      removePhotoText: {
        type: String,
        value: "Remove photo"
      },
      // ERRORS
      /**
       * Only images allowed error message (default: 'Only images allowed')
       */
      errorOnlyImagesAllowed: {
        type: String,
        value: "Only images allowed"
      },
      /**
       * Only one file allowed error message (default: 'Only one file can be uploaded')
       */
      errorOneFile: {
        type: String,
        value: "Only one file can be uploaded"
      },
      /**
       * Only one file allowed error message (default: 'Images of this format are not supported')
       */
      errorImageFormat: {
        type: String,
        value: "Images of this format are not supported"
      },
      /**
       * Only one file allowed error message (default: 'This file is too big. Please try a smaller file')
       */
      errorTooBig: {
        type: String,
        value: "This file is too big. Please try a smaller file"
      },
      /**
       * Additional text that will appear in parentheses after the label (example: `optional` )
       */
      optional: {
        type: String
      }
    };
  }

  ready() {
    super.ready();
    this.errorMessages = {
      singleFile: this.errorOneFile,
      fileTypeNotSupported: this.errorOnlyImagesAllowed,
      fileFormatNotSupported: this.errorImageFormat,
      fileTooBig: this.errorTooBig
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("iron-overlay-opened", this._handleModalPosition);
    this.addEventListener("iron-overlay-closed", this._revertModalPosition);

    // drag n drop
    this.$.droppingArea.addEventListener(
      "dragenter",
      this._handleDragEnter.bind(this)
    );
    this.$.droppingArea.addEventListener(
      "dragleave",
      this._handleDragLeave.bind(this)
    );
  }

  _handleChanges() {
    this.set("isCircle", this.circle && this.imageUrl);
  }

  _onDisabledChange(newVal, oldVal) {
    if (newVal) {
      this.$.removeButton.className += " disabled";
      this.$.droppingArea.className += " disabled";
    } else {
      this.$.removeButton.className = this.$.removeButton.className
        .split(/\s?disabled/)
        .join("");
      this.$.droppingArea.className = this.$.removeButton.className
        .split(/\s?disabled/)
        .join("");
    }
  }

  _handleThemeChange(newVal, oldVal) {
    this.isBasic = newVal === "basic";
    this.isProfile = newVal === "profile";
    this.$.uploadWrapper.className = this.$.uploadWrapper.className
      .split(oldVal)
      .join("");
    this.$.uploadWrapper.className += ` ${newVal}`;
    this._handleChanges();
  }

  _handleDragover(e) {
    e.preventDefault();
  }

  _handleDrop(e) {
    e.preventDefault();
    this._handleFileSelection(e);
    this._handleDragLeave();
  }

  _handleDragEnter(e) {
    if (this.$.droppingArea.className.indexOf("drag-active") === -1)
      this.$.droppingArea.className += " drag-active";
  }

  _handleDragLeave(e) {
    this.$.droppingArea.className = this.$.droppingArea.className
      .split(/\s?drag-active/)
      .join("");
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("iron-overlay-opened", this._handleModalPosition);
    this.removeEventListener("iron-overlay-closed", this._revertModalPosition);

    // drag n drop
    this.$.droppingArea.removeEventListener(
      "dragenter",
      this._handleDragEnter.bind(this)
    );
    this.$.droppingArea.removeEventListener(
      "dragleave",
      this._handleDragLeave.bind(this)
    );
  }

  // move modals to the body level
  _handleModalPosition(e) {
    if (e.target.localName === NsUploadImage.is) {
      let cropModal = this.$.croppieModal;
      let confirmationModal = this.$.confirmationModal;

      if (cropModal) document.body.appendChild(cropModal);
      if (confirmationModal) document.body.appendChild(confirmationModal);
    }
  }

  // move modals back to the component level
  _revertModalPosition(e) {
    if (e.target.localName === NsUploadImage.is) {
      let cropModal = document.body.querySelector("ns-upload-image-crop-modal");
      let confirmationModal = document.body.querySelector(
        "ns-upload-image-confirmation-modal"
      );

      if (cropModal) this.$.appendChild(cropModal);
      if (confirmationModal) this.$.appendChild(confirmationModal);
    }
  }

  // check if image url is valid. If so, show image
  _userHasImageUrl(newVal, oldOld) {
    if (newVal) {
      let img = new Image();
      img.onload = (res) => {
        this.$.ironPages.select(1);
      };
      img.src = newVal;
      this.set("isCircle", this.circle && newVal);
    } else {
      this.$.ironPages.select(0);
    }
  }

  _handleFileSelection(e) {
    let files =
      e.target.files && e.target.files.length
        ? e.target.files
        : e.dataTransfer.files;

    this.checkForErrors(files);
    if (this.errors.length) return;
    this.showCroppie();
  }

  _handleCancel(e) {
    this.$.ironPages.selectPrevious();
  }

  /**
   * Adjusts aspect ratio if output dimensions are bigger than containing modal can fit
   */
  adjustAspectRatio() {
    // has to match CSS // TODO: grab actual CSS values from DOM elements
    let modalSideMargin = 40;
    let modalTopMargin = 24;
    let modalPadding = 15;
    let additionalPadding = 10;
    let otherHeights = 135; //other heights includes modal header, croppie's zoom tool etc.

    if (this.circle && this.outputHeight !== this.outputWidth)
      this.outputHeight = this.outputWidth;

    let newWidth =
      this.outputWidth +
        modalSideMargin * 2 +
        modalPadding * 2 +
        additionalPadding * 2 >
      document.body.offsetWidth
        ? document.body.offsetWidth -
          (modalSideMargin * 2 + modalPadding * 2 + additionalPadding * 2)
        : this.outputWidth;

    let newHeight =
      this.outputHeight +
        modalTopMargin * 2 +
        modalPadding * 2 +
        additionalPadding * 2 +
        otherHeights >
      window.innerHeight
        ? window.innerHeight -
          (modalTopMargin * 2 +
            modalPadding * 2 +
            additionalPadding * 2 +
            otherHeights)
        : this.outputHeight;

    if (this.outputWidth > newWidth) {
      if (this.circle) newHeight = newWidth;
      else {
        // calculate aspect ratio for height based on new width
        if (this.outputHeight > newHeight) {
          if (this.circle) newWidth = newHeight;
          else {
            // calculate aspect ratio for width based on new height
            let precentage = newHeight / this.outputHeight;
            newWidth = this.outputWidth * precentage;
          }
        } else {
          let precentage = newWidth / this.outputWidth;
          newHeight = this.outputHeight * precentage;
        }
      }
    }
    if (this.outputHeight > newHeight) {
      if (this.circle) newWidth = newHeight;
      else {
        // calculate aspect ratio for width based on new height
        let precentage = newHeight / this.outputHeight;
        newWidth = this.outputWidth * precentage;
      }
    }

    return { newHeight, newWidth, additionalPadding };
  }

  /**
   * Prepares data and shows modal with cropping tool
   */
  showCroppie() {
    let reader = new FileReader();

    // wait for image base64 link to become available
    reader.onloadend = function() {
      this.img = new Image();
      this.img.src = reader.result;
      let data = this.adjustAspectRatio();

      let croppieData = {
        viewport: {
          width: data.newWidth,
          height: data.newHeight,
          type: this.circle ? "circle" : this.square
        },
        boundary: {
          width: data.newWidth + data.additionalPadding,
          height: data.newHeight + data.additionalPadding
        },
        enforceBoundary: true,
        enableOrientation: true,
        mouseWheelZoom: "ctrl"
      };

      this.croppieInstance = new Croppie(
        this.$.croppieModal.croppieElement.querySelector(
          "#croppingAreaContainer"
        ),
        croppieData
      );
      this.croppieInstance.data.bound = true;
      this.croppieInstance.bind({
        url: this.img.src
      });

      this.$.croppieModal.toggle();

      // since Croppie is appearing inside a modal, time after time when Croppie constructor is running it "thinks" that Croppie is "not visible",
      // which breaks everything. So to avoid it, there is a timeout to refresh croppie after 100ms
      setTimeout(() => {
        this.croppieInstance.refresh();
      }, 100);
    }.bind(this);

    reader.readAsDataURL(this.file);
  }

  back() {
    this.$.ironPages.selectIndex(0);
  }

  async _handleImageSave(e) {
    let args = [];

    // asssemble args for required dimension version of the image
    let heightStep = parseInt(this.outputHeight / 3);
    let widthStep = parseInt(this.outputWidth / 3);
    // lg
    args.push({
      size: [this.outputWidth, this.outputHeight],
      quality: 1,
      prefix: "_large"
    });
    // md
    if (this.medium)
      args.push({
        size: [this.outputWidth - widthStep, this.outputHeight - heightStep],
        quality: 0.5,
        prefix: "_medium"
      });
    // sm
    if (this.small)
      args.push({
        size: [
          this.outputWidth - widthStep * 2,
          this.outputHeight - heightStep * 2
        ],
        quality: 0.1,
        prefix: "_small"
      });

    this.images = {};

    for (let i = 0; i < args.length; i++) {
      await this.croppieInstance
        .result({
          type: "blob",
          size: { width: args[i].size[0], height: args[i].size[1] },
          quality: args[i].size
        })
        .then(
          function(res) {
            this.images[args[i].prefix] = res;
          }.bind(this)
        );
    }

    this.dispatchEvent(
      new CustomEvent("ns-image-upload-result", {
        detail: this.images
      })
    );
    this.$.croppieModal.toggle();

    let keys = Object.keys(this.images);
    // grab the smallest image
    let file = this.images[keys[keys.length - 1]];
    let reader = new FileReader();
    // wait for image base64 link to become available
    reader.onloadend = function() {
      this.imageUrl = reader.result;
      this.croppieInstance.destroy();
      this.$.file.value = "";
    }.bind(this);
    reader.readAsDataURL(file);
  }

  _handleRemoveImage(e) {
    this.$.file.value = "";
    this.$.confirmationModal.toggle();
  }

  _removeImage() {
    this.$.confirmationModal.toggle();
    this.$.ironPages.selectIndex(0);
    this.dispatchEvent(
      new CustomEvent("ns-image-upload-remove", { detail: this.imageUrl })
    );
  }

  _cancelImageRemoval() {
    this.$.confirmationModal.toggle();
  }

  _cancelImageCrop() {
    this.$.file.value = "";
    this.$.croppieModal.toggle();
    this.croppieInstance.destroy();
  }

  checkForErrors(fileList) {
    if (!fileList.length) return;

    this.errors = [];

    // file count check
    if (fileList.length > 1) {
      this.push("errors", this.errorMessages.singleFile);
      return;
    }
    this.file = fileList[0];

    // file type check
    let fileType = this.file.type.split("image/");
    if (fileType.length === 1) {
      this.push("errors", this.errorMessages.fileTypeNotSupported);
      return;
    }

    // size check
    let maxFileSize = this.maxSize * 1024 * 1000;
    if (this.file.size > maxFileSize) {
      this.push("errors", this.errorMessages.fileTooBig);
    }

    // image format check
    if (this.allowedFormats.indexOf(this.file.type) === -1) {
      this.push("errors", this.errorMessages.fileFormatNotSupported);
    }
  }

  getAllowedFormats() {
    let formats = [];
    this.allowedFormats.forEach((format) => {
      formats.push(format.split("image/")[1].toUpperCase());
    });
    return formats.join(", ");
  }

  getAllowedSize() {
    return `${this.maxSizeLabel} - ${this.maxSize} MB`;
  }
}

window.customElements.define(NsUploadImage.is, NsUploadImage);
